import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import PhotoPermissionsUtil from '../../../../../helpers/permissions/PhotoPermissionsUtil';
import answerYes from '../../../../../assets/images/answer-yes.svg';
import answerNo from '../../../../../assets/images/answer-no.svg';
import answerNA from '../../../../../assets/images/answer-na.svg';
import answerYesSelected from '../../../../../assets/images/answer-yes-selected.svg';
import answerNoSelected from '../../../../../assets/images/answer-no-selected.svg';
import answerNASelected from '../../../../../assets/images/answer-na-selected.svg';

import AnalyticsLogger from '../../../../../helpers/analytics/analyticsLogger';
import BaseComponent from '../../../../../baseClasses/BaseComponent';
import CommentPopup from '../CommentPopup';
import dataModel from '../../../../../models/DataModel';
import FileUtil from '../../../../../helpers/FileUtil/FileUtil';
import MiscConstants from '../../../../../constants/MiscConstants';
import Metadata from '../../../../../helpers/metadata/Metadata';
import ModalBannerConstants from '../../../../../constants/ModalBannerConstants';
import StepApproval from '../../TaskCompletionSteps/StepApproval';
import TaskCompletionStepComments from '../TaskCompletionStepComments';
import TaskCompletionStepPhotoGrid from '../PhotoUpload/TaskCompletionStepPhotoGrid';
import PhotoUtil from '../../../../../helpers/PhotoUtil/PhotoUtil';
import DataSource from '../../../../../dao/DataSource/DataSource';
import AttachmentUploading from '../../../../AttachmentUploading';

import './Inspection.scss';

const completionCiteriaSelector = {
  completion_criteria: {
    attachments: {
      additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'],
      conversions: {},
      user: {},
    },
    comments: { user: { additionalFields: ['profile_image'] } },
    event_histories: { activity: {}, activity_sub_type: {}, user_who_performed_action: {} },
    form_fields: {},
  },
};

export class Inspection extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      stepState: '',
      userCanModify: false,
      previewFile: null,
      completionStep: this.props.completionStep,
    };
    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
    this.acceptedFileTypes = FileUtil.getAcceptedExtensionsByType('jpg', false, true);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.showUploadModal = this.showUploadModal.bind(this);
  }

  handleCSUpdate(completionStep, action, acData) {
    this.setState({ completionStep });
  }

  componentDidMount() {
    const { completionStep } = this.props;

    this.csDataSource = new DataSource('CompletionCriterion', completionStep.id, completionCiteriaSelector, this.handleCSUpdate);
    this.csDataSource.get();
  }

  componentWillUnmount() {
    if (this.csDataSource) {
      this.csDataSource.release();
      this.csDataSource = null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.completionStep != this.state.completionStep ||
      prevState.completionStep.completed_at != this.state.completionStep.completed_at ||
      prevProps.isFieldUser != this.props.isFieldUser
    ) {
      this.setState({ uploadImage: false, uploadImageId: null, response: '' });
    }
    if (this.props.attachmentUploading !== this.state.attachmentUploading) {
      this.setState({ attachmentUploading: this.props.attachmentUploading });
    }
  }

  handleSaveFile(file, fileExtension, resizedImage, exifData) {
    const completionStep = _.clone(this.state.completionStep);
    
    if (completionStep.attachments.length >= MiscConstants.MAX_NUMBER_OF_PHOTOS) {
      return;
    }
    dataModel.attachmentUploading = true;
    const eventMetadatum = Metadata.generateMetadatum({ exifData: exifData });
    let attachment = {};
    attachment.id = dataModel.attachmentsDAO.creationIndex;
    attachment.attachable_type = 'CompletionCriterion';
    attachment.attachable_id = completionStep.id;
    attachment.upload = resizedImage;
    attachment.name = file.name;
    attachment.photo_longitude = null;
    attachment.photo_latitude = null;
    attachment.photo_taken_at = null;
    // below concat is to ensure re-render takes place (i.e. ensure attachments changes)
    completionStep.attachments.push(attachment);
    dataModel.attachmentsDAO.createAttachment(attachment, false, eventMetadatum);
  }

  showUploadModal() {
    let { completionStep } = this.state;
    if (!completionStep) {
      completionStep = this.props.completionStep;
    }
    dataModel.showGlobalModal(ModalBannerConstants.FILE_UPLOADER, {
      acceptedFileTypes: this.acceptedFileTypes,
      handleFileSelect: this.handleFileSelect,
      currentNumberOfFiles: completionStep.attachments.length,
      maximumAllowedFiles: MiscConstants.MAX_NUMBER_OF_PHOTOS,
    });
  }

  handleFileSelect(fileToUpload) {
    const { completionStep } = this.props;
    // one more check to ensure they haven't yet reached upload limit...
    if (completionStep.attachments.length < MiscConstants.MAX_NUMBER_OF_PHOTOS) {
      let extension = FileUtil.getExtension(fileToUpload.name);
      this.setState({ uploadMethod: 'web' });
      PhotoUtil.handleResizeFile(fileToUpload, extension, null, 'web')
        .then(parsedFile => {
          const { file, resizedImage, exifData } = parsedFile;
          return PhotoUtil.handleSaveFileOnWeb(this.props.task, this.props.completionStep, file, resizedImage, exifData);
        });
    }
  }

  handleNoPhotoRequiredButton() {
    this.setState({ noPhotoRequiredComment: true });
  }

  handleKeyDown(e) {
    if (e.keyCode == 27) {
      this.setState({ noPhotoRequiredComment: false, rejectionComment: false });
    } else if (e.keyCode == 13 || e.keyCode == 40) {
      e.preventDefault();
      this.handleCommentSent('reject', this.state.commentText);
    }
  }

  handleCancelComment() {
    this.setState({
      noPhotoRequiredComment: false,
      rejectionComment: false,
    });
  }

  handleCommentSent(type, commentText) {
    this.setState({ noPhotoRequiredComment: false, rejectionComment: false });
    
    const { completionStep } = this.state;
    this.csDataSource.performAction('rejectCompletionCriterion', completionStep, commentText);
  }

  handleCommentChange(e) {
    this.setState({ commentText: e.target.value });
  }

  handleStepApprove() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;
    if (userStepPermissions.has('is_approver')) {
      this.handleApproved(completionStep);
    }
    else if (userStepPermissions.has('is_reviewer')) {
      this.handleReviewed(completionStep);
    }
  }

  handleStepReject() {
    const { userStepPermissions, task } = this.props;
    if (userStepPermissions.has('accept/reject')) {
      if ((task.has_reviewer && this.state.completionStep.reviewed_at) || !task.has_reviewer){
        this.handleRejectApproval(this.state.completionStep);
      }
      else{
        this.handleRejectReview(this.state.completionStep);
      }
    }
    if (this.props.handleParentUpdate) {
      this.props.handleParentUpdate(this.props.task, this.state.completionStep, true);
    }

  }

  handlePhotoSelection(val) {
    this.setState({ photoSelected: val });
  }

  handleReviewed(completionStep) {
    this.csDataSource.performAction('reviewCompletionCriterion', completionStep);
  }

  handleRejectReview(completionStep) {
    this.setState({ rejectionComment: true });
  }

  handleApproved(completionStep) {
    this.csDataSource.performAction('approveCompletionCriterion', completionStep);
  }

  handleRejectApproval(completionStep) {
    this.setState({ rejectionComment: true });
  }

  setFocus(element) {
    if (element) {
      this.commentInput = element;
      element.focus();
    }
  }

  handleChangeAnswer() {
    const stepName = this.state.completionStep.name
      .split(' ')
      .map(
        word => word.substr(0, 1).toUpperCase() + word.substr(1).toLowerCase()
      )
      .join(' ');

    const message = (
      <div>
        <p>Are you sure you want to uncheck the completion step {stepName}?</p>
      </div>
    );

    this.props.dataModel.showGlobalModal('SYSTEM_POPUP', {
      height: 184,
      width: 320,
      message: message,
      cancelButtonText: 'Cancel',
      okButtonText: 'Yes',
      handleOwnerOk: () => {
        this.handleCompleteStep();
      },
      handleOwnerCancel: () => {
        this.cancelChange();
      },
    });
  };

  cancelChange = () => {
    this.props.dataModel.systemPopup = null;
    this.setState({ checked: true });
  };

  handleCompleteStep(response) {
    if (this.props.permissions.canCompleteTask && (this.props.userStepPermissions.has('step_complete') || this.props.userStepPermissions.has('override'))) {
      this.csDataSource.performAction('completeCompletionCriterion', this.state.completionStep, response);
      this.setState({ completionStep: { ...this.state.completionStep, response: response ? response : null } });
    }
  }

  handleAnswerSelected(e, response) {
    const { completionStep } = this.state;
    if (completionStep && completionStep.status !== 'not_started' && completionStep.response == response) {
      this.handleChangeAnswer();
    }
    else {
      if(response === 'NO'){
        if(completionStep.attachments.length > 0 && completionStep.comments.length > 0){
          this.handleCompleteStep(response);
        }
        else{
          dataModel.showGlobalModal('SYSTEM_POPUP', {
            height: 184,
            width: 320,
            message: 'A photo and comment is required before proceeding',
            oneButton: true,
            okButtonText: 'OK',
            handleOwnerOk: () => {
            },
          });
        }
      }
      else if(completionStep.attachment_required == true){
        if(response === 'N/A'){
          this.handleCompleteStep(response);
        }
        else if(completionStep.attachments && completionStep.attachments.length >= 1){
          this.handleCompleteStep(response);
        }
        else{
          dataModel.showGlobalModal('SYSTEM_POPUP', {
            height: 184,
            width: 320,
            message: 'A photo is required before proceeding',
            oneButton: true,
            okButtonText: 'OK',
            handleOwnerOk: () => {
            },
          });
        }
      }
      else{
        this.handleCompleteStep(response);
      }
    }
  }

  render() {
    const {
      editing,
      task,
      userStepPermissions,
    } = this.props;

    let { completionStep } = this.state;
    if (!completionStep) {
      completionStep = this.props.completionStep;
    }
    let buttons = [];

    let canAnswerQuestion = !this.props.projectTemplate && this.props.permissions.canCompleteTask && (this.props.userStepPermissions.has('step_complete') || this.props.userStepPermissions.has('override'));

    let reviewApproveButtons = null;
    if (!editing && !this.state.rejectionComment &&
      userStepPermissions.has('accept/reject') && !completionStep.rejected_at) {
      reviewApproveButtons = (
        <div className={'photo-approve-reject-buttons'}>
          <StepApproval key={(task.has_reviewer && (userStepPermissions.has('is_reviewer') || this.props.currentUser.super_admin) &&
            !completionStep.reviewed_at ? '_review' : '_approve') + completionStep.id} userStepPermissions={userStepPermissions}
            handleStepReject={this.handleStepReject}
            handleStepApprove={this.handleStepApprove}
          />
        </div>
      );
    }

    let typeImage = null;
    if (completionStep.attachments.length > 0) {
      typeImage = (
        <TaskCompletionStepPhotoGrid
          completionStep={completionStep}
          task={task}
          attachments={completionStep.attachments}
          status={this.props.status}
          uploadImageId={this.state.uploadImageId}
          withAcceptReject={reviewApproveButtons ? true : false}
          userStepPermissions={this.props.userStepPermissions}
        />
      );
    }
    
    let buttonList;
    if (!editing) {
      if (
        !editing &&
        PhotoPermissionsUtil.canUserEditPhoto(userStepPermissions, completionStep, task) &&
        (!completionStep.attachments || completionStep.attachments.length < MiscConstants.MAX_NUMBER_OF_PHOTOS)) 
      {
        buttons.push(
          <div
            className="button add-button"
            key="add-button"
            onClick={this.showUploadModal}
          >
          </div>
        );
      }

      buttonList = (
        <div
          className={
            `photo-button-list 
            ${completionStep.attachments && completionStep.attachments.length > 1 ? 'grid-expand ' : ''} 
            ${completionStep.attachments.length == 1 ? 'single-photo' : ''} 
            ${userStepPermissions.has('accept/reject')}`
          }
        >
          {buttons}
        </div>
      );
    }

    let uploading = <AttachmentUploading/>;

    let displayComponent = typeImage;

    let  attachmentRequired = null;
    
    if(!((completionStep.status === 'completed' || completionStep.status === 'pending_review') 
        && completionStep.response === 'N/A')){
      attachmentRequired=(
        <div className="photo-requires">A photo is required before proceeding</div>
      );
    }

    let commentDisplay = null;
    if (!editing) {
      if (this.state.rejectionComment) {
        buttonList = null;
        const element = document.getElementById(
          'task-completion-step-comments-' + completionStep.id
        );
        const rect = element.getBoundingClientRect();
        commentDisplay = (
          <CommentPopup
            task={task}
            completionStep={completionStep}
            height={element.clientHeight}
            width={element.clientWidth}
            left={rect.left}
            top={rect.top - 20}
            type={'REJECTED'}
            commentSent={commentText =>
              this.handleCommentSent('reject', commentText)
            }
            placeholder={
              'You must leave a comment to reject a Completion Step.'
            }
            cancelComment={this.handleCancelComment}
          />
        );
      }
    }

    if (this.state.photoSelected) {
      buttonList = null;
    }

    let comments = null;
    if (!editing) {
      comments = (
        <TaskCompletionStepComments
          id={'task-completion-step-comments-' + completionStep.id}
          task={task}
          completionStep={completionStep}
        />
      );
    }

    let answerButtons = null;
    if(completionStep.status === 'not_started'){
      answerButtons = <div className="answer-buttons">
          <img className={'button' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerNA} onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'N/A') : null} alt="na" data-qa='na' />
          <img className={'button' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerYes} onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'YES') : null} alt="yes" data-qa='yes' />
          <img className={'button' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerNo} onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'NO') : null} alt="no" data-qa='no' />
          {!this.state.attachmentUploading && buttonList}
      </div>;
    }
    else{
      switch (completionStep.response) {
      case 'YES':
        answerButtons = <div className="answer-buttons" >
            <img className="button unselected" src={answerNA} alt="na" data-qa='na' />
            <img className={'button selected' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerYesSelected} alt="yes" data-qa='yes-selected' onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'YES') : null} />
            <img className="button unselected" src={answerNo} alt="no" data-qa='no' />
            {!this.state.attachmentUploading && buttonList}
          </div>;
        break;
      case 'NO':
        answerButtons = <div className="answer-buttons">
            <img className="button unselected" src={answerYes} alt="yes" data-qa='yes' />
            <img className={'button selected' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerNoSelected} alt="no" data-qa='no-selected' onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'NO') : null}  />
            <img className="button unselected" src={answerNA} alt="na" data-qa='na' />
            {!this.state.attachmentUploading && buttonList}
          </div>;
        break;
      case 'N/A':
        answerButtons = <div className="answer-buttons">
            <img className="button unselected" src={answerNo} alt="no" data-qa='no' />
            <img className={'button selected' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerNASelected} alt="na" data-qa='na-selected' onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'N/A') : null}/>
            <img className="button unselected" src={answerYes} alt="yes" data-qa='yes' />
            {!this.state.attachmentUploading && buttonList}
          </div>;
        break;
      default:
        answerButtons = <div className="answer-buttons">
            <img className={'button' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerNA} onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'N/A') : null} alt="na" data-qa='na' />
            <img className={'button' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerYes} onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'YES') : null} alt="yes" data-qa='yes' />
            <img className={'button' + (!canAnswerQuestion ? ' not-editable' : '')} src={answerNo} onClick={canAnswerQuestion ? e => this.handleAnswerSelected(e, 'NO') : null} alt="no" data-qa='no' />
            {!this.state.attachmentUploading && buttonList}
          </div>;
      }
    }

    if (this.props.projectTemplate){
      commentDisplay = null;
      comments = null;
      reviewApproveButtons = null;
      buttonList = null;
    }
    return (
      <div
        className="inspection-step"
        id={'inspection-step-' + completionStep.id}
      >
        <div className={'display-and-buttons'}>
          {completionStep.instructions && (
              <div className="photo-instructions">
                <div className="instruction-label">Question</div>
                <div className="instruction-text">
                  {completionStep.instructions}
                </div>
              </div>
            )}
          {answerButtons}
          {!this.state.attachmentUploading ? displayComponent : uploading}
        </div>
        {
          completionStep.attachments.length >= MiscConstants.MAX_NUMBER_OF_PHOTOS &&
          <span className="max-photos-text">Maximum number of photos reached. Delete an image if you wish to upload more. </span>
        }
        {completionStep.attachment_required == true && 
          (completionStep.attachments && completionStep.attachments.length < 1) &&
          attachmentRequired}
        {commentDisplay}
        {reviewApproveButtons}
        {comments}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    dataModel: dataModel,
    inProgress: state.main.inProgress,
    permissions: state.new_users ? state.new_users.permissions : null,
    currentUser: state.new_users ? state.new_users.currentUser : null,
    currentCompany: state.new_companies ? state.new_companies.currentCompany : null,
    attachmentUploading: state.main.attachmentUploading,
  };
};

// For handling state change from a dispatch
export default connect(mapStateToProps, null)(Inspection);



// WEBPACK FOOTER //
// ./src/components/TaskCRUD/TaskCompletionStepsContainer/TaskCompletionSteps/Inspection/index.js